import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { contactData } from '../data/siteData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [form, setForm] = useState({ email: '', companyName: '', datasetNeeds: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/contact`, form);
      if (res.data.success) {
        setStatus('success');
        setTimeout(() => { setForm({ email: '', companyName: '', datasetNeeds: '' }); setStatus('idle'); }, 4000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF7A00]/70 mb-4 block">{contactData.sectionTag}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">{contactData.title}</h2>
            <p className="text-base text-white/40 max-w-md">{contactData.subtitle}</p>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center gap-4 py-16 bg-[#141414] border border-white/10 rounded-2xl">
                <CheckCircle size={40} className="text-[#FF7A00]" />
                <p className="text-sm text-white/60 text-center max-w-sm">{contactData.successMessage}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label className="block text-xs font-mono tracking-wider uppercase text-white/40 mb-2">Email Address *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="you@company.com"
                    className="w-full px-4 py-3 bg-[#141414] border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#FF7A00]/40 transition-colors"
                    data-testid="contact-email" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-wider uppercase text-white/40 mb-2">Company Name *</label>
                  <input type="text" name="companyName" value={form.companyName} onChange={handleChange} required placeholder="Your Company"
                    className="w-full px-4 py-3 bg-[#141414] border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#FF7A00]/40 transition-colors"
                    data-testid="contact-company" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-wider uppercase text-white/40 mb-2">Dataset Needs *</label>
                  <textarea name="datasetNeeds" value={form.datasetNeeds} onChange={handleChange} required rows={5}
                    placeholder="Describe the type of embodied data you need — environments, tasks, modalities, volume..."
                    className="w-full px-4 py-3 bg-[#141414] border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#FF7A00]/40 transition-colors resize-none"
                    data-testid="contact-needs" />
                </div>
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle size={16} /><span>{contactData.errorMessage}</span>
                  </div>
                )}
                <button type="submit" disabled={status === 'loading'}
                  className="group flex items-center gap-2 btn-gradient px-7 py-3.5 font-semibold text-sm rounded-full disabled:opacity-50"
                  data-testid="contact-submit">
                  {status === 'loading' ? 'Submitting...' : 'Submit Request'}
                  <Send size={14} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
